// The job server's CV page (/jobnet/cv/{id}) shown in the profile panel, restyled like the
// Talentspace design: the portal's font and colours, places and occupational groups as tags.
// Works only when the page is ours (production, same origin).

import { addFrameStyle } from '@/sections/frameBar';

const PIN_ICON = '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">'
  + '<path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></svg>';

// The groups come as one comma-joined text, but names have commas too
// ("… Krankenpflege, Rettungsdienst und Geburtshilfe", "Gesundheitsberufe, a.n.g."):
// - when the names carry their level ("– komplexe Tätigkeiten"), a group ends at its level
//   (a few groups have none, so a part starting with "Berufe " always opens a new one)
// - a part starting in lower case belongs to the one before
export const splitOccupationalGroups = (text: string): string[] => {
  const parts = text.split(/,\s+/).map((part) => part.trim()).filter(Boolean);
  const hasLevels = parts.some((part) => part.includes(' – '));
  const groups: string[] = [];
  let current = '';
  parts.forEach((part) => {
    const lowerCase = /^[a-zäöü]/.test(part);
    const levelPending = hasLevels && !current.includes(' – ') && !part.startsWith('Berufe ');
    const continues = current !== '' && (lowerCase || levelPending);
    current = continues ? `${current}, ${part}` : part;
    if (continues) groups[groups.length - 1] = current;
    else groups.push(current);
  });
  return groups;
};

const makeTag = (page: Document, text: string, withPin: boolean): HTMLElement => {
  const tag = page.createElement('span');
  tag.className = 'ts-cv-tag';
  tag.innerHTML = withPin ? PIN_ICON : '';
  tag.append(page.createTextNode(text));
  return tag;
};

// Text right inside the element (not in its heading child). A Google translation wraps it
// in <font> tags, those count as the element's own text too.
const isOwnText = (node: ChildNode) => node.nodeType === Node.TEXT_NODE || (node as Element).tagName === 'FONT';

const ownText = (element: Element): string => Array.from(element.childNodes)
  .filter(isOwnText)
  .map((node) => node.textContent || '')
  .join(' ')
  .replace(/\s+/g, ' ')
  .trim();

const replaceOwnText = (element: Element, tags: HTMLElement[]) => {
  Array.from(element.childNodes)
    .filter((node) => isOwnText(node) || (node as Element).tagName === 'BR')
    .forEach((node) => node.remove());
  const list = element.ownerDocument.createElement('div');
  list.className = 'ts-cv-tags';
  list.append(...tags);
  element.append(list);
};

const cvCss = (font: string, color: (name: string, fallback: string) => string) => `
  * { font-family: ${font} !important; color: ${color('--ts-text', '#495057')}; }
  /* The <font> wrappers of a Google translation take the look of the text they sit in */
  font { font-size: inherit !important; font-weight: inherit !important; line-height: inherit !important; color: inherit !important; }
  .container_prof, .container_prof p, .container_prof li, .container_prof div { font-size: 15px; line-height: 1.6; }
  .container_prof { margin-top: 32px !important; }
  h1 { font-size: 32px !important; line-height: 1.2 !important; font-weight: 700 !important; color: ${color('--ts-dark', '#111')}; margin-bottom: 12px; }
  h1 .age { font-size: inherit !important; line-height: inherit !important; font-weight: 400 !important; color: ${color('--ts-muted', '#6b6b6b')}; }
  h2 { font-size: 20px !important; font-weight: 700 !important; color: ${color('--ts-dark', '#111')}; }
  .line { display: none; }
  .location { font-size: 15px !important; margin: 0 0 12px !important; }
  .groups_head { font-size: 12px !important; font-weight: 600 !important; letter-spacing: .06em; color: ${color('--ts-muted', '#6b6b6b')}; margin-bottom: 8px; }
  .prof_group, .location_add { margin-top: 24px !important; }
  .big_line_2 { border-bottom-color: ${color('--ts-border', '#e5e5e5')} !important; margin: 32px 0 !important; }
  .ts-cv-tags { display: flex; flex-wrap: wrap; gap: 8px; }
  .ts-cv-tag { display: inline-flex; align-items: center; gap: 6px; padding: 4px 14px; border: 1px solid ${color('--ts-border', '#e5e5e5')};
    border-radius: 999px; background: ${color('--ts-bg-soft', '#f4f4f4')}; color: ${color('--ts-dark', '#111')}; font-size: 14px !important; line-height: 1.5 !important; }
  .ts-cv-tag svg { flex-shrink: 0; }
`;

// The job server's contact form ("Direkt kontaktieren", /jobnet/ang/contact_form.php): a fixed
// 600px table with the labels under the fields. It opens inside the CV page (or as its own page
// after sending); laid out here as a two-column form in the portal's look.
const contactFormCss = (font: string, color: (name: string, fallback: string) => string) => `
  body { font-family: ${font} !important; }
  h2 { font-size: 24px !important; font-weight: 700 !important; color: ${color('--ts-dark', '#111')}; margin: 0 0 12px !important; }
  form[name="contact_form"] { max-width: 760px; font-family: ${font} !important; font-size: 14px; color: ${color('--ts-muted', '#6b6b6b')}; }
  form[name="contact_form"] > p { margin: 0; }
  form[name="contact_form"] b { color: ${color('--ts-dark', '#111')}; }
  form[name="contact_form"] table, form[name="contact_form"] tbody { display: block; width: 100%; margin-top: 16px; }
  form[name="contact_form"] tr { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
  form[name="contact_form"] td { display: block; padding: 0 !important; font-size: 13px; line-height: 1.4; }
  form[name="contact_form"] td[colspan] { grid-column: 1 / -1; }
  form[name="contact_form"] td br { display: none; }
  form[name="contact_form"] input[type="text"], form[name="contact_form"] .cc-input, form[name="contact_form"] select {
    display: block; width: 100% !important; box-sizing: border-box; min-height: 42px; margin-bottom: 6px; padding: 9px 12px;
    border: 1px solid ${color('--ts-border', '#e5e5e5')}; border-radius: 8px; background: #fff;
    font-family: ${font} !important; font-size: 15px; color: ${color('--ts-dark', '#111')}; outline: none; }
  form[name="contact_form"] .cc-input.multi_lines { min-height: 120px; }
  form[name="contact_form"] input[type="text"]:focus, form[name="contact_form"] .cc-input:focus {
    border-color: ${color('--portal-primary', '#f2b32e')}; box-shadow: 0 0 0 3px ${color('--theme-color-25-rgb', 'rgba(242, 179, 46, .25)')}; }
  form[name="contact_form"] input[type="submit"], form[name="contact_form"] input[type="button"] {
    float: right; margin: 8px 0 0 12px !important; padding: 10px 24px; border-radius: 8px; cursor: pointer;
    font-family: ${font} !important; font-size: 15px; font-weight: 600; }
  form[name="contact_form"] input[type="submit"] {
    border: 1px solid ${color('--portal-primary', '#f2b32e')}; background: ${color('--portal-primary', '#f2b32e')}; color: #fff; }
  form[name="contact_form"] input[type="button"] {
    border: 1px solid ${color('--ts-dark', '#111')}; background: #fff; color: ${color('--ts-dark', '#111')}; }
  @media (max-width: 575.98px) { form[name="contact_form"] tr { grid-template-columns: 1fr; } }
`;

const portalLook = () => {
  const rootStyle = getComputedStyle(document.documentElement);
  return {
    font: getComputedStyle(document.body).fontFamily,
    color: (name: string, fallback: string) => rootStyle.getPropertyValue(name).trim() || fallback,
  };
};

// Added to every page of the frame, so the form is styled however it opens
export const restyleCvContactForm = (page: Document | null) => {
  if (!page || page.getElementById('ts-cv-contact-form')) return;
  const { font, color } = portalLook();
  addFrameStyle(page, 'ts-cv-contact-form', contactFormCss(font, color));
};

// Safe to call again: the style goes in once, the tags only while the page has none
// (a translation brings the plain text back)
export const restyleCvPage = (page: Document | null) => {
  if (!page?.querySelector('.container_prof')) return;

  // The portal's own font and design colours
  if (!page.getElementById('ts-cv-design')) {
    const { font, color } = portalLook();
    addFrameStyle(page, 'ts-cv-design', cvCss(font, color));
  }
  if (page.querySelector('.main_info .ts-cv-tags')) return;

  // Places as tags with a pin
  const location = page.querySelector('.main_info > .location');
  if (location && ownText(location)) {
    const places = ownText(location).split(/,\s*/).filter(Boolean);
    replaceOwnText(location, places.map((place) => makeTag(page, place, true)));
  }

  // Occupational groups as tags
  const groups = page.querySelector('.main_info .prof_group');
  if (groups && ownText(groups)) {
    replaceOwnText(groups, splitOccupationalGroups(ownText(groups)).map((group) => makeTag(page, group, false)));
  }
};
